import type { ImagePlaceholderElement as ImagePlaceholder } from '@/typings/customilyElements/canvasElements';

const computeContainScale = (containerW: number, containerH: number, sourceW: number, sourceH: number) => {
    if (!containerW || !containerH || !sourceW || !sourceH) return 1;
    return Math.min(containerW / sourceW, containerH / sourceH);
};

/**
 * Extracts face coordinates from metadata.
 * Returns the bounding box and center points only if exactly one face is detected.
 */
const extractFaceBox = (metadata?: {
    faceAnalysis?: {
        faces?: number[][];
    };
}) => {
    const faces = metadata?.faceAnalysis?.faces;

    // Ensure exactly one face is detected
    if (!Array.isArray(faces) || faces.length !== 1) {
        return null;
    }

    const face = faces[0];

    // Expected structure: 8 values representing p1(x,y), p3(x,y), p2(x,y), p4(x,y)
    if (!Array.isArray(face) || face.length < 8) {
        return null;
    }

    const xCoords = [face[0], face[2], face[4], face[6]].map(Number);
    const yCoords = [face[1], face[3], face[5], face[7]].map(Number);

    // Validation to ensure coordinates are valid numbers
    if (xCoords.some(isNaN) || yCoords.some(isNaN)) {
        return null;
    }

    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    const width = maxX - minX;
    const height = maxY - minY;

    // Ensure the bounding box has valid dimensions
    if (width <= 0 || height <= 0) {
        return null;
    }

    return {
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
        maxY: maxY,
        width: width,
        height: height,
        area: width * height
    };
};

/**
 * Dynamically adjusts the image by relating the detected face points
 * with the placeholder's initialization values (intent).
 */
export const applyFaceAdjustment = (object: ImagePlaceholder, parsedMetadata?: Record<string, unknown>): void => {
    const faceBox = extractFaceBox(parsedMetadata);
    if (!faceBox) return;

    const imgElement = object.originalElement;
    const imgW = imgElement?.naturalWidth || imgElement?.width;
    const imgH = imgElement?.naturalHeight || imgElement?.height;

    // Safety check for image dimensions
    if (!imgW || !imgH) return;

    const boundsRect = object.item(1);
    if (!boundsRect) return;

    const previousEditMode = object._editMode;
    object._editMode = true;

    try {
        // Use defaults for init values to avoid NaN
        const initH = object.initHeight || boundsRect.height || 0;
        const initCX = object.initCenterX || 0;
        const initCY = object.initCenterY || 0;

        // 1. DYNAMIC SCALE CALCULATION
        const targetScale = initH / faceBox.height;
        const baseScale = computeContainScale(boundsRect.width || 0, boundsRect.height || 0, imgW, imgH);

        // Prevent division by zero
        const relativeScale = baseScale > 0 ? targetScale / baseScale : 1;
        object.setEditModeImageScale(relativeScale);

        // 2. DYNAMIC POSITION CALCULATION
        // Align face bottom (maxY) with design intent bottom
        const initBottomY = initCY + initH / 2;

        // Calculate offsets in natural image pixels
        // Formula: (TargetPosInCanvas / Scale) - (InternalPosInImage - ImageHalf)
        let offsetX = initCX / targetScale - (faceBox.centerX - imgW / 2);
        let offsetY = initBottomY / targetScale - (faceBox.maxY - imgH / 2);

        // Rotate offset to compensate for object rotation
        // When object has rotation, the offset must be transformed to the rotated coordinate system
        const angle = object.angle || 0;
        if (angle !== 0) {
            const radians = (angle * Math.PI) / 180;
            const cosAngle = Math.cos(radians);
            const sinAngle = Math.sin(radians);
            // Store original values before transforming
            const originalOffsetX = offsetX;
            const originalOffsetY = offsetY;
            offsetX = originalOffsetX * cosAngle - originalOffsetY * sinAngle;
            offsetY = originalOffsetX * sinAngle + originalOffsetY * cosAngle;
        }

        // Validation: Ensure values are finite before applying
        if (Number.isFinite(offsetX) && Number.isFinite(offsetY)) {
            object.setEditModeImageOffset(offsetX, offsetY);
        }
        object._updateRelationshipMatrix();
    } catch (error) {
        console.error('Error applying face adjustment:', error);
    } finally {
        object._editMode = previousEditMode;
    }
};
